import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import type { AppArea } from '../../config/areas';
import { areaSections } from '../../config/areas';

interface ManagementSidebarProps {
  areas: readonly AppArea[];
  roles: readonly string[];
  open: boolean;
  onClose: () => void;
}

interface SidebarNavProps {
  areas: readonly AppArea[];
  roles: readonly string[];
  onNavigate: () => void;
}

/** Grouped section links, one heading per area - reused for both the desktop and off-canvas layouts. */
function SidebarNav({ areas, roles, onNavigate }: SidebarNavProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-5">
      {areas.map((area) => {
        const sections = areaSections(area, roles);
        if (sections.length === 0) return null;
        const headingId = `management-sidebar-${area.key}`;

        return (
          <div key={area.key} role="group" aria-labelledby={headingId}>
            <p id={headingId} className="px-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              {t(`nav.areas.${area.key}`)}
            </p>
            <ul className="mt-1 flex flex-col gap-0.5">
              {sections.map((section) => (
                <li key={section.to}>
                  <NavLink
                    to={section.to}
                    end
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `block rounded px-3 py-1.5 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${
                        isActive
                          ? 'bg-muted text-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`
                    }
                  >
                    {t(section.labelKey)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/** Persistent left-hand nav for the management shell, off-canvas below the lg breakpoint. */
export function ManagementSidebar({ areas, roles, open, onClose }: ManagementSidebarProps) {
  const { t } = useTranslation();

  return (
    <>
      <nav aria-label={t('nav.management')} className="hidden w-56 shrink-0 lg:block">
        <SidebarNav areas={areas} roles={roles} onNavigate={onClose} />
      </nav>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label={t('nav.closeMenu')}
            onClick={onClose}
            className="absolute inset-0 bg-foreground/40"
          />
          <nav
            id="management-sidebar-mobile"
            aria-label={t('nav.management')}
            className="absolute inset-y-0 left-0 w-64 overflow-y-auto border-r border-border bg-surface p-4"
          >
            <div className="mb-4 flex items-center justify-between">
              <span className="font-heading font-semibold text-foreground">{t('nav.management')}</span>
              <button
                type="button"
                aria-label={t('nav.closeMenu')}
                onClick={onClose}
                className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <XMarkIcon className="size-5" aria-hidden="true" />
              </button>
            </div>
            <SidebarNav areas={areas} roles={roles} onNavigate={onClose} />
          </nav>
        </div>
      )}
    </>
  );
}
